import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layers, Trash2, Loader2, ChevronDown, ChevronUp, ArrowRight, AlertTriangle, Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_LABEL = {
  l2: 'L2',
  l3: 'L3',
  l4: 'L4',
  hierarchy_collision: 'Hierarchy',
};

const SOURCE_BADGE = {
  fuzzy_auto: { label: 'Auto-merge', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  hierarchy_collision: { label: 'Collision', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  user_confirmed: { label: 'Confirmed', className: 'bg-green-50 text-green-700 border-green-200' },
};

export const CategoryMergeRules = ({ token }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/data-refinery/taxonomy-pack`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `taxonomy_pack_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.rule_count} rules`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const mode = window.confirm(
      'Import mode:\n\nOK = MERGE (add to existing rules)\nCancel = REPLACE (wipe existing rules first)'
    ) ? 'merge' : 'replace';
    setImporting(true);
    try {
      const text = await file.text();
      const pack = JSON.parse(text);
      const res = await axios.post(`${API}/data-refinery/taxonomy-pack/import?mode=${mode}`, pack, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      toast.success(`Imported ${res.data.imported} rules (${mode} mode)`);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Import failed');
    } finally { setImporting(false); }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/spend/category-merge-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRules(res.data.rules || []);
    } catch (err) {
      console.error('Failed to load category merge rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete this rule?\n\n"${rule.original}" → "${rule.canonical}"\n\nFuture classifications will no longer auto-apply this merge.`)) return;
    setDeletingId(rule.id);
    try {
      await axios.delete(`${API}/spend/category-merge-rules/${rule.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success(`Rule deleted: "${rule.original}" → "${rule.canonical}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete rule');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-2 text-sm text-slate-500" data-testid="category-rules-loading">
        <Loader2 size={14} className="animate-spin" /> Loading category rules...
      </div>
    );
  }

  if (rules.length === 0) {
    // Still expose Import so empty tenants can seed from a pack
    return (
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="category-merge-rules-empty">
        <input
          type="file" accept=".json,application/json" ref={fileInputRef}
          className="hidden" onChange={handleImport} data-testid="import-pack-input"
        />
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-slate-100 border border-slate-200 rounded-sm flex items-center justify-center">
              <Layers size={16} className="text-slate-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">No Category Normalization Rules yet</h3>
              <p className="text-xs text-slate-500">Import a vocabulary pack to seed canonical names, or run classification to generate them automatically.</p>
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="gap-1.5 text-xs"
            data-testid="import-taxonomy-pack-button-empty"
          >
            {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Import Pack
          </Button>
        </div>
      </div>
    );
  }

  // Group rules by source for the summary line
  const fuzzyCount = rules.filter(r => r.source === 'fuzzy_auto').length;
  const collisionCount = rules.filter(r => r.source === 'hierarchy_collision').length;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="category-merge-rules">
      <input
        type="file"
        accept=".json,application/json"
        ref={fileInputRef}
        className="hidden"
        onChange={handleImport}
        data-testid="import-pack-input"
      />
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200">
        <Button
          size="sm" variant="outline"
          onClick={handleExport}
          disabled={exporting || rules.length === 0}
          className="gap-1.5 text-xs"
          data-testid="export-taxonomy-pack-button"
          title="Download all merge rules as a JSON pack for import into another tenant"
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          Export Vocabulary Pack
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          className="gap-1.5 text-xs"
          data-testid="import-taxonomy-pack-button"
          title="Import a vocabulary pack (JSON) from another tenant or an industry starter template"
        >
          {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          Import Vocabulary Pack
        </Button>
      </div>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
        data-testid="category-rules-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-indigo-50 border border-indigo-100 rounded-sm flex items-center justify-center">
            <Layers size={16} className="text-indigo-600" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-slate-900">
              Active Category Normalization Rules
              <span className="ml-2 text-xs text-slate-500 font-normal">({rules.length})</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {fuzzyCount > 0 && <span>{fuzzyCount} fuzzy auto-merge{fuzzyCount === 1 ? '' : 's'}</span>}
              {fuzzyCount > 0 && collisionCount > 0 && <span> · </span>}
              {collisionCount > 0 && <span>{collisionCount} hierarchy collision{collisionCount === 1 ? '' : 's'}</span>}
              {fuzzyCount + collisionCount === 0 && <span>Click to view</span>}
              <span> · applied to all future classifications</span>
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm" data-testid="category-rules-table">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Level</th>
                <th className="text-left px-4 py-2 font-medium">Original → Canonical</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rules.map(rule => {
                const isCollision = rule.source === 'hierarchy_collision';
                const badge = SOURCE_BADGE[rule.source] || SOURCE_BADGE.fuzzy_auto;
                return (
                  <tr key={rule.id} className="hover:bg-slate-50/50" data-testid={`category-rule-${rule.id}`}>
                    <td className="px-4 py-2.5">
                      <span className="text-xs font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                        {LEVEL_LABEL[rule.level] || rule.level}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {isCollision ? (
                        <div className="flex items-center gap-2 text-slate-700">
                          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                          <span className="font-medium">"{rule.original}"</span>
                          <span className="text-xs text-slate-500">— used at L2 + L3, kept at L3 only</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 line-through">{rule.original}</span>
                          <ArrowRight size={12} className="text-slate-400" />
                          <span className="text-slate-900 font-medium">{rule.canonical}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-sm border ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(rule)}
                        disabled={deletingId === rule.id}
                        className="text-red-500 hover:bg-red-50 hover:text-red-600 h-7 px-2"
                        data-testid={`delete-category-rule-${rule.id}`}
                      >
                        {deletingId === rule.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
